import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cn } from '@/lib/utils'
import { Button } from './button'

interface ConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'default'
  loading?: boolean
  onConfirm: () => void
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  loading = false,
  onConfirm,
}: ConfirmDialogProps) {
  const isDanger = variant === 'danger'

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm animate-in fade-in duration-200" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-[60] w-[calc(100%-2rem)] max-w-sm -translate-x-1/2 -translate-y-1/2',
            'rounded-2xl border border-white/[0.08] bg-neutral-950 p-6 shadow-2xl',
            'animate-in fade-in zoom-in-95 duration-200'
          )}
        >
          <DialogPrimitive.Title className="text-base font-semibold text-white">
            {title}
          </DialogPrimitive.Title>

          {description && (
            <DialogPrimitive.Description className="mt-2 text-sm text-neutral-400 leading-relaxed">
              {description}
            </DialogPrimitive.Description>
          )}

          <div className="mt-6 flex gap-3">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="flex-1 h-10 text-sm text-neutral-400 hover:text-white border border-white/[0.08] hover:bg-white/[0.04]"
            >
              {cancelLabel}
            </Button>
            <Button
              onClick={() => {
                onConfirm()
              }}
              disabled={loading}
              className={cn(
                'flex-1 h-10 text-sm font-medium',
                isDanger
                  ? 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20'
                  : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/20'
              )}
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                confirmLabel
              )}
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
